import { useState, useEffect, useCallback } from 'react'
import { ConfigVersionHistoryButton } from '../components/ConfigVersionHistory'
import { Link, useParams } from 'react-router-dom'
import { type LoginResponse } from '../api/auth'
import { contestsApi, type ContestDetail } from '../api/performance'
import { contestParticipantsApi, contestAwardsApi, type ContestAward, type SuggestedWinner } from '../api/contests'
import {
  FormGrid,
  PaneLayout, StatGrid, KeyValueList, Page, PageHeader, Callout, Card, CardHeader, CardContent, StatCard, DataTable, LoadingState, EmptyState,
  StatusPill, Button, IconButton, TextField, Icon, useSnackbar, type PillTone, useConfirm,
} from '../components/ui'

const CONTEST_TYPE_LABELS: Record<string, string> = {
  revenue: 'Fatturato', volume: 'Volume contratti', product: 'Prodotto specifico',
}

function statusBadge(s: string) {
  const map: Record<string, { tone: PillTone; label: string }> = {
    draft: { tone: 'neutral', label: 'Bozza' },
    active: { tone: 'success', label: 'Attiva' },
    completed: { tone: 'info', label: 'Completata' },
    cancelled: { tone: 'error', label: 'Annullata' },
  }
  const entry = map[s] ?? { tone: 'neutral' as PillTone, label: s }
  return <StatusPill tone={entry.tone}>{entry.label}</StatusPill>
}

function formatDate(s: string): string {
  return new Date(s).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function formatValue(contestType: string, value: number): string {
  if (contestType === 'volume') return `${value} contratti`
  return value.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
}

function rankTone(rank: number): PillTone {
  return rank === 1 ? 'warning' : 'neutral'
}

/** TASK-0089: `ContestsController.Get` now OMITS `currentValue` and `rank` from every participant
 * when the caller has no `CanSeeRealAmounts` (and is not Admin) — the leaderboard values are real
 * per-agent contract revenue, the same ADR-008-sensitive figures `ContractsController` never hands
 * to a non-privileged agent. Both fields are absent keys, not nulls/zeros, so rendering them raw
 * would print `undefined` / `#undefined` and `formatValue` would produce `NaN`.
 *
 * Same degradation `PaymentsPage.tsx`/`ReportsPage.tsx`/`CommissionsPage.tsx`'s `AmountCell`
 * (TASK-0090) already uses for the equivalent Finance redaction — a muted lock glyph with a
 * tooltip — reused rather than reinvented. Kept as two small local components (not imported from
 * those pages, which do not export theirs) because the two cells render different things when the
 * value IS present: a currency/count string vs. a coloured rank badge.
 *
 * The props are typed `number | undefined` even though `api/performance.ts` still declares
 * `currentValue: number` / `rank: number` as required: that file is outside this task's
 * allowed_paths, and passing a `number` into a `number | undefined` parameter is exactly what makes
 * the runtime guard below type-check without an inline `=== undefined` comparison the compiler
 * would reject as impossible (TS2367). Flagged to the Lead — the API types should be narrowed to
 * `currentValue?: number` / `rank?: number` in a follow-up, the way TASK-0090 already did for
 * `api/finance-ext.ts`. */
function RedactedLock({ title }: { title: string }) {
  return (
    <span title={title}>
      <Icon name="lock" size={18} />
    </span>
  )
}

function ValueCell({ contestType, value }: { contestType: string; value: number | undefined }) {
  if (value === undefined) return <RedactedLock title="Valore non visibile" />
  return <>{formatValue(contestType, value)}</>
}

function RankCell({ rank }: { rank: number | undefined }) {
  if (rank === undefined) return <RedactedLock title="Posizione in classifica non visibile" />
  return <StatusPill tone={rankTone(rank)}>#{rank}</StatusPill>
}

/** Same Admin-tier check `CompanyDetailPage.tsx` (TASK-0035) / `FormDetailPage.tsx` (TASK-0036) use
 * — `auth.role === 'Administrator' || auth.role === 'EqualAdmin'`, the two role values
 * `PolicyNames.Admin` grants server-side. Reused verbatim here for the same reason those two pages
 * reuse it: one real precedent for role-gating a UI element in this codebase, not a new mechanism. */
function isAdmin(auth: LoginResponse): boolean {
  return auth.role === 'Administrator' || auth.role === 'EqualAdmin'
}

interface Props {
  auth: LoginResponse
}

/** Admin-only participant management — mirrors `CompanyDetailPage.tsx`'s `MergePanel` shape (a
 * collapsible card with a small form) for the "add" side, plus a remove button per leaderboard row.
 * `POST /api/contests/{id}/participants` and `DELETE /api/contests/{id}/participants/{participantId}`
 * are both `[Authorize(PolicyNames.Admin)]` server-side (`ContestsController.cs`) — this panel is not
 * itself a security boundary, just keeping the action out of view for callers who cannot use it. */
function AddParticipantPanel({ contestId, onAdded }: { contestId: number; onAdded: () => void }) {
  const [open, setOpen] = useState(false)
  const [userId, setUserId] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    const parsed = parseInt(userId, 10)
    if (isNaN(parsed)) {
      setError('Inserisci un ID utente valido.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await contestParticipantsApi.add(contestId, parsed)
      setUserId('')
      setOpen(false)
      onAdded()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore durante aggiunta partecipante.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card variant="outlined">
      <CardHeader
        icon="person_add"
        title="Aggiungi partecipante"
        headingLevel={3}
        actions={
          !open ? (
            <Button variant="outlined" size="sm" onClick={() => setOpen(true)}>
              Aggiungi
            </Button>
          ) : undefined
        }
      />
      {open && (
        <CardContent>
          {error && <Callout tone="error">{error}</Callout>}
          <form onSubmit={e => void handleAdd(e)}>
            <FormGrid className="md-form-grid--end">
              <div>
                <TextField
                  id="add-participant-user-id"
                  label="ID utente (agente)"
                  type="number"
                  value={userId}
                  onChange={e => setUserId(e.target.value)}
                  required
                />
              </div>
              <div className="d-flex gap-2">
                <Button type="submit" variant="tonal" size="sm" disabled={saving}>
                  {saving ? 'Aggiunta...' : 'Conferma'}
                </Button>
                <Button
                  variant="text"
                  size="sm"
                  disabled={saving}
                  onClick={() => setOpen(false)}
                >
                  Chiudi
                </Button>
              </div>
            </FormGrid>
          </form>
        </CardContent>
      )}
    </Card>
  )
}

/**
 * F-142 (REQ-028-007): premi della gara per posizione in classifica. Nessun importo: nome e descrizione sono testo.
 * Lo staff vede tutti i premi; un agente riceve dal server SOLO i propri (e solo la propria assegnazione), quindi la
 * stessa lista vale per tutti. Scrivere (creare, proporre i vincitori, assegnare, consegnare, annullare) e' dell'Admin:
 * i pulsanti sono nascosti agli altri, ma il confine e' il server (403).
 */
function AwardsPanel({ contestId, admin }: { contestId: number; admin: boolean }) {
  const confirm = useConfirm()
  const [awards, setAwards] = useState<ContestAward[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [suggestions, setSuggestions] = useState<Record<number, SuggestedWinner[]>>({})
  const [form, setForm] = useState({ positionFrom: '1', positionTo: '', name: '', description: '' })
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    setError(null)
    contestAwardsApi.list(contestId)
      .then(setAwards)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Errore nel caricamento dei premi.'))
      .finally(() => setLoading(false))
  }, [contestId])

  useEffect(() => { load() }, [load])

  async function run(action: () => Promise<unknown>) {
    setError(null)
    try { await action(); load() }
    catch (e) { setError(e instanceof Error ? e.message : 'Operazione non riuscita.') }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    const from = parseInt(form.positionFrom, 10)
    const to = form.positionTo.trim() === '' ? undefined : parseInt(form.positionTo, 10)
    if (isNaN(from) || (to !== undefined && isNaN(to))) { setError('Posizione non valida.'); return }
    setSaving(true)
    await run(async () => {
      await contestAwardsApi.create(contestId, {
        positionFrom: from, ...(to !== undefined ? { positionTo: to } : {}),
        name: form.name, ...(form.description.trim() ? { description: form.description.trim() } : {}),
      })
      setForm({ positionFrom: '1', positionTo: '', name: '', description: '' })
    })
    setSaving(false)
  }

  async function handleSuggest(awardId: number) {
    await run(async () => {
      const res = await contestAwardsApi.assign(contestId, awardId, {})
      setSuggestions(prev => ({ ...prev, [awardId]: res.suggestedWinners }))
    })
  }

  return (
    <Card variant="outlined">
      <CardHeader icon="military_tech" title="Premi" headingLevel={3} />
      <CardContent>
        {error && <Callout tone="error">{error}</Callout>}
        {loading ? (
          <LoadingState />
        ) : awards.length === 0 ? (
          <div className="text-muted">{admin ? 'Nessun premio definito.' : 'Nessun premio assegnato a te.'}</div>
        ) : (
          awards.map(a => (
            <div key={a.id} className="border-bottom pb-2 mb-2">
              <div className="d-flex justify-content-between align-items-start gap-2">
                <div>
                  <strong>{a.name}</strong>{' '}
                  <StatusPill tone="neutral">
                    {a.positionFrom === a.positionTo ? `${a.positionFrom}° posto` : `dal ${a.positionFrom}° al ${a.positionTo}° posto`}
                  </StatusPill>{' '}
                  <StatusPill tone="neutral">{a.status}</StatusPill>
                  {a.description && <div className="text-muted md-typescale-body-small">{a.description}</div>}
                </div>
                {admin && a.status !== 'Annullato' && (
                  <div className="d-flex gap-1">
                    <Button variant="outlined" size="sm" onClick={() => void handleSuggest(a.id)}>
                      Proponi vincitori
                    </Button>
                    {a.assignments.length > 0 && a.status !== 'Consegnato' && (
                      <Button variant="outlined" size="sm"
                        onClick={() => void run(() => contestAwardsApi.deliver(contestId, a.id, {}))}>
                        Consegna
                      </Button>
                    )}
                    <IconButton icon="delete" size="sm" aria-label="Annulla il premio"
                      onClick={() => { void (async () => { if (await confirm({ title: 'Annullare il premio?', message: 'Annullare o rimuovere questo premio?', confirmLabel: 'Conferma', destructive: true })) void run(() => contestAwardsApi.remove(contestId, a.id)) })() }} />
                  </div>
                )}
              </div>
              {a.assignments.map(x => (
                <div key={x.id} className="md-typescale-body-small mt-1">
                  Assegnato a utente #{x.userId} il {formatDate(x.assignedAt)}
                  {x.deliveredAt ? <>{' '}<StatusPill tone="success">consegnato il {formatDate(x.deliveredAt)}</StatusPill></> : null}
                </div>
              ))}
              {admin && suggestions[a.id] && (
                <div className="mt-2">
                  <div className="md-typescale-body-small text-muted">Dalla classifica:</div>
                  {suggestions[a.id]!.length === 0 && <div className="md-typescale-body-small text-muted">Nessun partecipante in queste posizioni.</div>}
                  {suggestions[a.id]!.map(w => (
                    <div key={w.userId} className="d-flex align-items-center gap-2 md-typescale-body-small">
                      <span>#{w.rank} &middot; utente #{w.userId}</span>
                      <Button variant="outlined" size="sm" disabled={w.alreadyAssigned}
                        onClick={() => void run(async () => {
                          await contestAwardsApi.assign(contestId, a.id, { userId: w.userId })
                          setSuggestions(prev => { const { [a.id]: _gone, ...rest } = prev; return rest })
                        })}>
                        {w.alreadyAssigned ? 'Assegnato' : 'Assegna'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
        {admin && (
          <form className="md-form-grid-host mt-3" onSubmit={e => void handleCreate(e)}>
            <div className="md-form-grid md-form-grid--end" data-columns="4">
            <div>
              <TextField id="award-from" label="Dalla pos." type="number" min={1} value={form.positionFrom}
                onChange={e => setForm(f => ({ ...f, positionFrom: e.target.value }))} required />
            </div>
            <div>
              <TextField id="award-to" label="Alla pos." type="number" min={1} value={form.positionTo}
                onChange={e => setForm(f => ({ ...f, positionTo: e.target.value }))} />
            </div>
            <div className="md-span-2">
              <TextField id="award-name" label="Premio" value={form.name} maxLength={200}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
            </div>
            <div className="md-span-2">
              <TextField id="award-description" label="Descrizione" value={form.description} maxLength={1000}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div>
              <Button type="submit" variant="tonal" size="sm" fullWidth disabled={saving}>Aggiungi premio</Button>
            </div>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  )
}

export function ContestDetailPage({ auth }: Props) {
  const snackbar = useSnackbar()
  const confirm = useConfirm()
  const { id: idStr } = useParams<{ id: string }>()
  const id = parseInt(idStr ?? '', 10)

  const [contest, setContest] = useState<ContestDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [removingId, setRemovingId] = useState<number | null>(null)

  const load = useCallback((silent = false) => {
    if (isNaN(id)) {
      setError('ID gara non valido.')
      setLoading(false)
      return
    }
    if (!silent) setLoading(true)
    setError(null)
    setNotFound(false)
    contestsApi
      .get(id)
      .then(data => setContest(data))
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err)
        if (msg.includes('404') || msg.toLowerCase().includes('not found')) {
          setNotFound(true)
        } else {
          setError(msg || 'Errore nel caricamento della gara.')
        }
      })
      .finally(() => { if (!silent) setLoading(false) })
  }, [id])

  useEffect(() => { load() }, [load])

  async function handleRemove(participantId: number) {
    if (!(await confirm({ title: 'Rimuovere il partecipante?', message: 'Rimuovere questo partecipante dalla gara?', confirmLabel: 'Rimuovi', destructive: true }))) return
    setRemovingId(participantId)
    try {
      await contestParticipantsApi.remove(id, participantId)
      load(true)
    } catch (err) {
      snackbar.show({ message: err instanceof Error ? err.message : 'Errore durante rimozione partecipante.', tone: 'error' })
    } finally {
      setRemovingId(null)
    }
  }

  if (loading) {
    return (
      <Page>
        <LoadingState />
      </Page>
    )
  }

  if (notFound) {
    return (
      <Page>
        <Callout tone="warning">
          Gara non trovata.{' '}
          <Link to="/contests">Torna alla lista</Link>
        </Callout>
      </Page>
    )
  }

  if (error) {
    return (
      <Page>
        <Callout tone="error">{error}</Callout>
      </Page>
    )
  }

  if (!contest) return null

  const c = contest

  return (
    <Page>
      <PageHeader
        title={c.name}
        icon="emoji_events"
        back={{ to: '/contests', label: 'Gare' }}
        subtitle={
          <span className="d-inline-flex align-items-center gap-2">
            {statusBadge(c.status)}
            <span>{CONTEST_TYPE_LABELS[c.contestType] ?? c.contestType}</span>
          </span>
        }
        actions={<ConfigVersionHistoryButton entityType="Contest" entityId={c.id} label={c.name} />}
      />

      <PaneLayout
        variant="supporting"
        main={<>
          <Card variant="outlined" padding="none">
            <CardHeader icon="leaderboard" title="Classifica" headingLevel={3} />
            {c.participants.length === 0 ? (
              <EmptyState icon="groups" title="Nessun partecipante" />
            ) : (
              <DataTable variant="plain">
                <thead>
                  <tr>
                    <th>Pos.</th>
                    <th>Utente</th>
                    <th className="md-num">Valore</th>
                    <th>Iscritto il</th>
                    {isAdmin(auth) && <th>Azioni</th>}
                  </tr>
                </thead>
                <tbody>
                  {c.participants.map(p => (
                    <tr key={p.id}>
                      <td>
                        <RankCell rank={p.rank} />
                      </td>
                      <td>Utente #{p.userId}</td>
                      <td className="md-typescale-emphasized-body-medium md-num">
                        <ValueCell contestType={c.contestType} value={p.currentValue} />
                      </td>
                      <td className="text-muted md-typescale-body-small">{formatDate(p.joinedAt)}</td>
                      {isAdmin(auth) && (
                        <td>
                          <IconButton
                            icon="delete"
                            size="sm"
                            aria-label="Rimuovi"
                            disabled={removingId === p.id}
                            onClick={() => void handleRemove(p.id)}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            )}
          </Card>

          {isAdmin(auth) && <AddParticipantPanel contestId={c.id} onAdded={() => load(true)} />}

          <AwardsPanel contestId={c.id} admin={isAdmin(auth)} />
        </>}
        pane={<>
          <StatGrid>
            <StatCard label="Periodo" value={`${formatDate(c.startDate)} → ${formatDate(c.endDate)}`} />
            <StatCard label="Partecipanti" value={c.participants.length} />
          </StatGrid>

          <Card variant="outlined">
            <CardHeader icon="info" title="Dettagli" headingLevel={3} />
            <CardContent>
              <KeyValueList items={[
                { label: 'Premio', value: c.prize || '—' },
                /* TASK-0161: targetValue is the declared goal (configuration data, same category as
                  prize/dates above) — NOT the same as the live per-participant currentValue/rank in
                  the leaderboard, which stay redacted per TASK-0089. Plain display only, no progress
                  percentage (explicitly out of scope — see ContestsController.Get's remarks). */
                { label: 'Obiettivo', value: c.targetValue != null ? formatValue(c.contestType, c.targetValue) : '—' },
                { label: 'Operatore target', value: c.targetOperator || '—' },
                { label: 'Tipo prodotto', value: c.targetProductType || '—' },
                { label: 'Descrizione', value: <span className="text-muted">{c.description || '—'}</span> },
              ]} />
            </CardContent>
          </Card>
        </>}
      />
    </Page>
  )
}
